import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import {
    ImageChooserBlock,
    GalleryBlock as ReactivatedGalleryBlock,
} from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import iconArrowLeftBlue from "../../../img/icons/arrow_left_blue.svg";
import iconArrowRightBlue from "../../../img/icons/arrow_right_blue.svg";
import iconCarouselArrowLeft from "../../../img/icons/carousel-arrow-left.svg";
import iconCarouselArrowRight from "../../../img/icons/carousel-arrow-right.svg";
import { isoSafeHTML } from "../../models/nominals";
import { usePageSetting } from "../../utils/settings";
import { Image } from "../Image";
import { RawHTML } from "../RawHTML";

import styles from "./Gallery.module.scss";

export type IGalleryData = NonNullable<
    ReactivatedGalleryBlock["galleries"]
>[number];

type ISlideContentBlock =
    | { id: string; type: "image"; value: ImageChooserBlock | null }
    | { id: string; type: "video_block"; value: string };

type ISlideValue = {
    content: readonly ISlideContentBlock[];
    caption: string | null;
    caption_style: string | null;
    caption_text_alignment: string | null;
    video_thumbnail: ImageChooserBlock | null;
    promo_period: { is_promo_active?: boolean } | null;
};

type ISlideWrapper = {
    id: string;
    type: string;
    value: ISlideValue;
};

type ISlideCollection = {
    title: string;
    collection: readonly ISlideWrapper[];
};

type IProps = Immutable<{
    gallery: IGalleryData;
    defaultThumbnail: string;
}>;

enum GalleryCaption {
    SEALY_BLUE = "gallery-caption--sealy-blue",
    SEALY_LIME_YELLOW = "gallery-caption--sealy-lime-yellow",
    ORIENT = "gallery-caption--orient",
    RAZZMATAZZ = "gallery-caption--razzmatazz",
    YELLOW = "gallery-caption--yellow",
    TEAL = "gallery-caption--teal",
}

enum GalleryTextAlignment {
    CENTER = "gallery-caption--text-center",
    RIGHT = "gallery-caption--text-right",
}

const areBooleanArraysEqual = (a: boolean[], b: boolean[]): boolean => {
    if (a.length !== b.length) {
        return false;
    }
    return a.every((value, index) => value === b[index]);
};

const scrollThumbnailIntoView = (
    container: HTMLDivElement | null,
    thumb: HTMLElement | null | undefined,
): void => {
    if (!container || !thumb) {
        return;
    }

    const containerRect = container.getBoundingClientRect();
    const imageRect = thumb.getBoundingClientRect();

    if (
        imageRect.left < containerRect.left ||
        imageRect.right > containerRect.right
    ) {
        // Scope to the strip — scrollIntoView can scroll the document too.
        const delta =
            imageRect.left < containerRect.left
                ? imageRect.left - containerRect.left
                : imageRect.right - containerRect.right;
        container.scrollBy({ left: delta, behavior: "smooth" });
    }
};

const getSlideImageLazyLoadSetting = (
    appSlug: string | null | undefined,
    lazy: boolean,
): boolean => {
    if (appSlug === "cocoon") {
        return false;
    }
    if (appSlug === "tempurpedic") {
        return lazy;
    }
    return true;
};

const renderSlideContent = (
    slide: Immutable<ISlideValue>,
    appSlug: string | null | undefined,
    lazy: boolean,
): React.ReactNode => {
    const captionClasses = classNames({
        [styles.galleryCaption]: !slide.caption_style,
        [styles.galleryCaptionSealyBlue]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.SEALY_BLUE,
        [styles.galleryCaptionSealyLimeYellow]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.SEALY_LIME_YELLOW,
        [styles.galleryCaptionOrient]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.ORIENT,
        [styles.galleryCaptionRazzmatazz]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.RAZZMATAZZ,
        [styles.galleryCaptionYellow]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.YELLOW,
        [styles.galleryCaptionTeal]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.TEAL,
        [styles.galleryCaptionTextCenter]:
            !!slide.caption_text_alignment &&
            slide.caption_text_alignment === GalleryTextAlignment.CENTER,
        [styles.galleryCaptionTextRight]:
            !!slide.caption_text_alignment &&
            slide.caption_text_alignment === GalleryTextAlignment.RIGHT,
    });
    const roundedCorner = classNames({
        [styles.galleryRoundedCorner]:
            !!slide.caption_style &&
            slide.caption_style === GalleryCaption.SEALY_LIME_YELLOW,
    });
    const content = slide.content as readonly ISlideContentBlock[];
    return content.map((contentBlock) => {
        if (contentBlock.type === "video_block") {
            return (
                <RawHTML
                    key={contentBlock.id}
                    className={styles.videoContainer}
                    html={isoSafeHTML.wrap(contentBlock.value)}
                />
            );
        }

        if (!contentBlock.value) {
            return null;
        }

        const mobileRendition = contentBlock.value.renditions?.mobile_img;
        return (
            <figure key={contentBlock.id}>
                <picture className={roundedCorner}>
                    {mobileRendition && (
                        <source
                            media={`(max-width: ${mobileRendition.width}px)`}
                            srcSet={mobileRendition.url}
                        />
                    )}
                    <Image
                        src={contentBlock.value.url}
                        alt={contentBlock.value.title}
                        lazy={getSlideImageLazyLoadSetting(appSlug, lazy)}
                    />
                </picture>
                {slide.caption && (
                    <figcaption className={captionClasses}>
                        {slide.caption}
                    </figcaption>
                )}
            </figure>
        );
    });
};

export const Gallery = React.memo((props: IProps): React.ReactNode => {
    const appSlug = usePageSetting("app_slug");

    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [currentProduct, setCurrentProduct] = React.useState<number | null>(
        props.gallery.product ?? null,
    );
    const [lazy, setLazy] = React.useState(false);
    const [visibleThumbs, setVisibleThumbs] = React.useState<boolean[]>([]);
    const visibleThumbsRef = React.useRef<boolean[]>([]);
    const [focusedThumbIndex, setFocusedThumbIndex] = React.useState<
        number | null
    >(null);

    const [previewRoot, updatePreviewRoot] =
        React.useState<HTMLDivElement | null>(null);
    const setPreviewRoot = React.useCallback(
        (root: HTMLDivElement | null): void => {
            updatePreviewRoot(root);
            if (root === null) {
                setFocusedThumbIndex(null);
            }
        },
        [],
    );
    const observerRef = React.useRef<IntersectionObserver | null>(null);
    const thumbElsRef = React.useRef<Map<number, HTMLButtonElement>>(new Map());
    const thumbIndexByElRef = React.useRef(new WeakMap<Element, number>());

    const galleryData = props.gallery
        .gallery as Immutable<ISlideCollection> | null;
    const slideCollection = galleryData?.collection ?? [];

    const galleryProduct = props.gallery.product ?? null;

    React.useEffect(() => {
        if (galleryProduct !== currentProduct) {
            setCurrentSlide(0);
            setCurrentProduct(galleryProduct);
        }
    }, [galleryProduct, currentProduct]);

    const publishThumbVisibility = React.useCallback(
        (visibilities: boolean[]) => {
            if (
                !areBooleanArraysEqual(visibleThumbsRef.current, visibilities)
            ) {
                visibleThumbsRef.current = visibilities;
                setVisibleThumbs(visibilities);
            }
        },
        [],
    );

    // Stable per-index ref callbacks (React 19 cleanup) so identity does not
    // change every render and force unobserve/observe of every thumb.
    const thumbRefCallbacks = React.useMemo(
        () =>
            Array.from(
                { length: slideCollection.length },
                (_, index) => (el: HTMLButtonElement | null) => {
                    if (!el) {
                        return;
                    }
                    thumbElsRef.current.set(index, el);
                    thumbIndexByElRef.current.set(el, index);
                    observerRef.current?.observe(el);
                    return () => {
                        observerRef.current?.unobserve(el);
                        thumbElsRef.current.delete(index);
                        thumbIndexByElRef.current.delete(el);
                    };
                },
            ),
        [slideCollection.length],
    );

    // Strip thumb visibility for inert: IntersectionObserver on the previews root.
    React.useEffect(() => {
        if (!previewRoot) {
            return;
        }
        const window = getWindow();
        if (!window) {
            return;
        }

        if (!window.IntersectionObserver) {
            return;
        }

        const observer = new window.IntersectionObserver(
            (entries) => {
                const length = slideCollection.length;
                // Fail-open: unknown / not-in-batch stays non-off-screen (`!== false`).
                const next = Array.from(
                    { length },
                    (_, i) => visibleThumbsRef.current[i] !== false,
                );
                for (const entry of entries) {
                    const idx = thumbIndexByElRef.current.get(entry.target);
                    if (idx === undefined) {
                        continue;
                    }
                    // Any visible pixel stays clickable; only fully off-canvas → inert.
                    next[idx] = entry.isIntersecting;
                }
                publishThumbVisibility(next);
            },
            { root: previewRoot, threshold: 0 },
        );
        observerRef.current = observer;
        thumbElsRef.current.forEach((el) => {
            observer.observe(el);
        });

        return () => {
            observer.disconnect();
            observerRef.current = null;
        };
    }, [previewRoot, slideCollection.length, publishThumbVisibility]);

    // Keep current thumb in the clip when the slide changes.
    React.useEffect(() => {
        scrollThumbnailIntoView(
            previewRoot,
            thumbElsRef.current.get(currentSlide),
        );
    }, [currentSlide, previewRoot]);

    const updateSlide = (slideIdx: number) => {
        setCurrentSlide(slideIdx);
        setLazy(true);
    };

    const prevSlide = () => {
        const newSlide =
            currentSlide <= 0 ? slideCollection.length - 1 : currentSlide - 1;
        updateSlide(newSlide);
    };

    const nextSlide = () => {
        const newSlide =
            currentSlide >= slideCollection.length - 1 ? 0 : currentSlide + 1;
        updateSlide(newSlide);
    };

    const currentSlideData = slideCollection[currentSlide]?.value;

    if (!currentSlideData) {
        return null;
    }

    return (
        <span className={styles.root}>
            {/* Keep the server and browser markup identical. CSS controls
                which navigation is visible at each viewport width. */}
            <div className={styles.slideCount}>
                {interpolate(t`%(idx)s/%(total)s`, {
                    idx: `${currentSlide + 1}`,
                    total: `${slideCollection.length}`,
                })}
            </div>
            <div className={styles.slideButtons} data-direction="previous">
                <button onClick={prevSlide} aria-label={t`Previous`}>
                    <SVG aria-hidden="true" src={iconCarouselArrowLeft} />
                </button>
            </div>

            {renderSlideContent(currentSlideData, appSlug, lazy)}

            <div className={styles.slideButtons} data-direction="next">
                <button onClick={nextSlide} aria-label={t`Next`}>
                    <SVG aria-hidden="true" src={iconCarouselArrowRight} />
                </button>
            </div>
            <div className={styles.thumbnailStrip}>
                <button
                    className={styles.navButton}
                    onClick={prevSlide}
                    aria-label={t`Previous`}
                >
                    <SVG aria-hidden="true" src={iconArrowLeftBlue} />
                </button>
                <div className={styles.previews} ref={setPreviewRoot}>
                    <div className={styles.inner}>
                        {slideCollection.map((slide, i) => {
                            const slideNum = i + 1;
                            const label = t`Slide ${slideNum}`;
                            const firstContentBlock = slide.value.content[0] as
                                | ISlideContentBlock
                                | undefined;
                            if (!firstContentBlock?.value) {
                                return null;
                            }
                            const videoThumb = slide.value.video_thumbnail;
                            const thumbnailURL =
                                firstContentBlock.type === "image"
                                    ? firstContentBlock.value?.renditions
                                          ?.thumbnail?.url
                                    : videoThumb?.renditions?.thumbnail?.url;
                            const isCurrentThumbnail = i === currentSlide;
                            const isOffScreen = visibleThumbs[i] === false;
                            const isFocusedThumbnail = focusedThumbIndex === i;
                            return (
                                <button
                                    key={i}
                                    ref={thumbRefCallbacks[i]}
                                    className={styles.previewButton}
                                    onClick={() => {
                                        updateSlide(i);
                                    }}
                                    onFocus={(event) => {
                                        setFocusedThumbIndex(i);
                                        scrollThumbnailIntoView(
                                            previewRoot,
                                            event.currentTarget,
                                        );
                                    }}
                                    onBlur={() => {
                                        setFocusedThumbIndex((prev) =>
                                            prev === i ? null : prev,
                                        );
                                    }}
                                    aria-current={isCurrentThumbnail}
                                    inert={
                                        isOffScreen &&
                                        !isCurrentThumbnail &&
                                        !isFocusedThumbnail
                                    }
                                >
                                    <img
                                        src={
                                            thumbnailURL ||
                                            props.defaultThumbnail
                                        }
                                        alt={label}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>
                <button
                    className={styles.navButton}
                    onClick={nextSlide}
                    aria-label={t`Next`}
                >
                    <SVG aria-hidden="true" src={iconArrowRightBlue} />
                </button>
            </div>
        </span>
    );
});
